import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ResponseListsService } from '../../shared/services/response-lists.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { ResponseTableService } from '../../shared/services/response-table.service';
import { Theme } from '../../../assets/styles/theme';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { FlyoutDataType, FlyoutOpeningData } from '../../flyout/flyout-models';
import { FlyoutService } from '../../flyout/flyout.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { LanguageService } from '../../core/language.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ThemeService } from '../../shared/services/theme.service';
import InputDefinitionHelper from './input-definition-helper';
import { ResponseListItem } from '../../shared/components/response-list-selector/response-list-selector.component';

@Component({
  selector: 'app-response-lists',
  templateUrl: './response-lists.component.html',
  styleUrls: ['./response-lists.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ResponseListsComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  public responseListsIsExpanded = false;
  public responseLists: Array<any>;
  public selectedResponseList: any;
  public theme = Theme;
  public statistics;
  public statisticsRows;
  public hasChartData = false;
  private loadFromParamsId = undefined;
  subscription: Subscription;
  private cutoffStatistics = 11;
  private isDarkThemeSub$;
  public responsesOverTime: ResponseStatistics;
  private customNameMapper = {};
  isDarkTheme: boolean;
  public responsesOverTimeDays = 30;
  private flyoutResponseList$: Subscription;
  private subscriptionArray: Subscription[] = [];
  public responseStatistics: any[];
  public isAllChatsShown: boolean;
  public isLoadingResponseList = true;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly responseListsService: ResponseListsService,
    private readonly responseTableService: ResponseTableService,
    private readonly snackBarService: SnackBarService,
    private readonly languageService: LanguageService,
    private readonly themeService: ThemeService,
    private readonly flyoutService: FlyoutService,
    private route: ActivatedRoute,
    private router: Router,
    private readonly snackbarService: SnackBarService,
  ) {
    this.logger = loggerFactory.get('ResponseListsComponent');
  }

  ngOnInit(): void {
    this.isDarkThemeSub$ = this.themeService.isDarkThemeBS.subscribe((item) => {
      this.isDarkTheme = item;
    });
    this.route.queryParams.subscribe((params) => {
      if (params?.id && params.id.length > 0) {
        this.loadFromParamsId = params.id;
      }
    });
    this.getResponseLists();
    this.flyoutResponseList$ = this.flyoutService.flyoutResponseListBS.subscribe((value) => {
      //When creating the first responselist, we need to set it.
      if (!this.selectedResponseList) {
        this.selectedResponseList = value;
      }
      if (value && value.name && value.name.length > 0) {
        let runColumnReload = false;
        if (value.columns.length !== this.selectedResponseList.columns.length) {
          runColumnReload = true;
        }
        this.selectedResponseList.columns = value.columns.filter(
          (x) => x.key !== null && x.key !== undefined && x.key.length > 0,
        );
        this.createCustomNameMapper();
        if (value.id !== this.selectedResponseList.id || runColumnReload) {
          // Switch responseList
          this.loadFromParamsId = value.id;
          this.responseLists = undefined;
          this.getResponseLists();
        }
        this.selectedResponseList.name = value?.name;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptionArray.push(this.flyoutResponseList$, this.isDarkThemeSub$);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  onResponseListSelected(item: ResponseListItem): void {
    this.selectedResponseList = item;
    this.selectResponseList();
  }

  private destroy$ = new Subject<void>();
  private async getResponseLists() {
    if (!this.responseLists) {
      this.responseListsService
        .getResponseListsWithStatistics()
        .pipe(takeUntil(this.destroy$))
        .subscribe((response) => {
          this.isLoadingResponseList = true;
          this.responseLists = response;
          this.loadResponseList();
          this.isLoadingResponseList = false;
        });
    }
  }

  private loadResponseList() {
    try {
      this.responseLists.map((x) => (x.lastItemAddedDate = x.lastItemAddedDate ? new Date(x.lastItemAddedDate) : null));
      this.logger.debug('responseLists ${responseLists}', this.responseLists);
      const listFromParams = this.responseLists.find((s) => s.id === this.loadFromParamsId);
      if (this.loadFromParamsId && listFromParams) {
        this.selectedResponseList = listFromParams;
        this.selectResponseList();
      } else {
        if (this.responseLists.length > 0) {
          // Falling back silently would show a different list under the id the user asked for.
          if (this.loadFromParamsId) {
            this.snackBarService.warning('response-lists.not-found-in-account');
          }
          const sorted = this.responseLists.sort((n1, n2) => n2.lastItemAddedDate - n1.lastItemAddedDate);
          this.selectedResponseList = sorted[0];
          this.selectResponseList();
        } else {
          this.router.navigate([], {
            queryParams: {
              id: null,
            },
            queryParamsHandling: 'merge',
          });
        }
      }
      this.responseLists.sort((n1, n2) => n2.itemsAdded - n1.itemsAdded);
    } catch {
      this.snackBarService.error('Could not get ResponseLists');
    }
  }

  private createCustomNameMapper() {
    this.selectedResponseList.columns.forEach((x) => {
      if (x.type === Type.CountrySelect) {
        const isGenericCountryPattern = x.name && /^Country\d+$/.test(x.name);
        const isCountryTranslation =
          x.name === this.languageService.translateInstantly('response-lists.columns.country');

        if (isGenericCountryPattern) {
          this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.country-code');
        } else if (x.name && !isGenericCountryPattern && x.name === x.key) {
          this.customNameMapper[x.key] = x.name;
        } else if (x.name && !isCountryTranslation) {
          this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.country');
        } else {
          this.customNameMapper[x.key] = [x.key];
        }
      } else if (x.name && x.name.length > 0) {
        this.customNameMapper[x.key] = x.name;
      } else {
        switch (x.type) {
          case Type.Text:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.Email:
            this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.email');
            break;
          case Type.FullName:
            this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.name');
            break;
          case Type.FirstName:
            this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.firstname');
            break;
          case Type.LastName:
            this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.lastname');
            break;
          case Type.Phone:
            this.customNameMapper[x.key] = this.languageService.translateInstantly('response-lists.columns.phone');
            break;
          case Type.RadioButtons:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.ButtonChoices:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.Checkboxes:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.DropDown:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.Date:
            this.customNameMapper[x.key] = x.key;
            break;
          case Type.CreatedDate:
            this.customNameMapper[x.key] = this.languageService.translateInstantly(
              'response-lists.columns.created-date',
            );
            break;
          case Type.Consent:
            break;
          case Type.Session:
            break;
          case Type.StarRating:
            break;
          case Type.SmileyRating:
            break;
        }
      }
    });
  }

  private async getResponsesOverTime() {
    this.responseTableService.getResponsesOverTime(this.selectedResponseList.id, this.responsesOverTimeDays).subscribe(
      (response) => {
        response.items.map((x) => (x['date'] = x['date'].substring(0, 10)));
        this.responsesOverTime = response;
      },
      (error) => {
        this.logger.error('Problem getting responses over time ' + JSON.stringify(error));
      },
    );
  }

  private async getResponseStatistics(responseListId: string) {
    const response = await this.responseTableService.getResponseStatistics(responseListId).toPromise();
    this.responseStatistics = response;
    this.statistics = [];
    for (const column of response) {
      const chartItems = <ChartItem[]>[];
      const chart = <Chart>{};

      chart.name = column.name;
      let total = 0;
      for (const data of column.values) {
        total += data.count as number;
      }

      if (total > 0) {
        InputDefinitionHelper.mapGraphKeysItemsTypeToInputDefinitionName(this.selectedResponseList.columns, column);
        for (const data of column.values) {
          this.hasChartData = true;
          const chartItem = <ChartItem>{};
          chartItem.value = data.count as number;
          if (total > 0) {
            chartItem.name = data.name + ' : ' + Math.round((chartItem.value / total) * 100) + '%';
          } else {
            chartItem.name = data.name;
          }
          chartItems.push(chartItem);
          chart.values = chartItems;
        }
        chart.values = chart.values.sort((a, b) => b.value - a.value);

        if (chart.values.length > this.cutoffStatistics) {
          const rest = chart.values.slice(this.cutoffStatistics);
          const totalValue = rest.reduce((partialSum, a) => partialSum + a.value, 0);
          chart.values.splice(this.cutoffStatistics);
          let name;
          if (total > 0) {
            name =
              this.languageService.translateInstantly('response-lists.statistics-other') +
              ' : ' +
              Math.round((totalValue / total) * 100) +
              '%';
          } else {
            name = this.languageService.translateInstantly('response-lists.statistics-other');
          }
          chart.values.push({
            value: totalValue,
            name: name,
          });
        }
        this.statistics.push(chart);
      }
    }

    // eslint-disable-next-line no-bitwise
    this.statisticsRows = (this.statistics.count / this.cutoffStatistics) | 0;
  }

  async selectResponseList() {
    this.logger.info('selected id  ' + this.selectedResponseList.id);
    this.sortColumns();
    this.hasChartData = false;
    this.isAllChatsShown = false;
    await this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: { id: this.selectedResponseList.id },
    });
    this.createCustomNameMapper();
    await this.getResponseStatistics(this.selectedResponseList.id);
    await this.getResponsesOverTime();
  }

  private sortColumns() {
    this.selectedResponseList.columns = this.selectedResponseList.columns.filter(
      (x) => x.key != null && x.key !== undefined && x.key.length > 0,
    );
    this.selectedResponseList.columns = this.selectedResponseList.columns
      .sort((a, b) => a.key.localeCompare(b.key))
      .filter((x) => x.key !== 'consent');
    this.selectedResponseList.columns.sort((a, b) => {
      const aValue = this.getSortValue(a.type);
      const bValue = this.getSortValue(b.type);
      return aValue - bValue;
    });
  }

  private getSortValue(type: Type) {
    switch (type) {
      case Type.Text:
        break;
      case Type.Email:
        return 1;
      case Type.FullName:
        return 4;
      case Type.FirstName:
        return 2;
      case Type.LastName:
        return 3;
      case Type.Phone:
        return 5;
    }
    return 10;
  }

  openCreateFlyout() {
    this.flyoutService.open({
      type: FlyoutDataType.EditResponseList,
    } as FlyoutOpeningData);
  }

  openEditFlyout() {
    this.flyoutService.open({
      type: FlyoutDataType.EditResponseList,
      responseListId: this.selectedResponseList.id,
    } as FlyoutOpeningData);
  }

  deleteList() {
    this.responseListsService.deleteResponseList(this.selectedResponseList.id).subscribe(
      () => {
        this.loadFromParamsId = undefined;
        this.responseLists = undefined;
        this.getResponseLists();
        this.snackbarService.success('response-lists.edit.delete-success');
      },
      () => {
        this.snackbarService.error('generic.error');
      },
    );
  }

  public showAllCharts(): void {
    this.isAllChatsShown = true;
    const elCharts = document.getElementsByClassName('piecharts-container');
    Array.from(elCharts).forEach((el) => el.classList.remove('hidden-chart'));
  }
}

export interface Column {
  name: string;
  key: string;
  type: Type;
  inputDefinitions: InputDefinitions;
}

export enum Type {
  Text = 'Text',
  Email = 'Email',
  FullName = 'FullName',
  FirstName = 'FirstName',
  LastName = 'LastName',
  Phone = 'Phone',
  RadioButtons = 'RadioButtons',
  ButtonChoices = 'ButtonChoices',
  Checkboxes = 'Checkboxes',
  DropDown = 'DropDown',
  Date = 'Date',
  CreatedDate = 'CreatedDate',
  Consent = 'Consent',
  Session = 'Session',
  Selection = 'Selection',
  StarRating = 'StarRating',
  SmileyRating = 'SmileyRating',
  Nps = 'Nps',
  Csat = 'Csat',
  CountrySelect = 'CountrySelect',
  ElapsedTimeMs = 'ElapsedTimeMs',
}

export interface InputDefinitions {
  name: string;
  key: string;
}

export interface Chart {
  name: string;
  values: ChartItem[];
}

export interface ChartItem {
  id?: string;
  value?: number;
  name?: string;
}

export interface ResponseStatistics {
  name?: string;
  count?: number;
  items?: Array<any>;
}
